import { logError } from '@ptek/client-logger';
import { useWidgetContext } from '@ptek/widget-core';
import { useEffect, useState } from 'react';
import { documentTemplatesClient } from '../api-clients/documentTemplates';
import { useWidgetAuth } from '../context/AuthContext';
import type { AmbientScribeContext } from '../types';
import { FEATURE_FLAGS } from '../types/featureFlags';
import { FormattedTemplate } from '../types/templates';
import { isFeatureEnabled } from '../utils/feature-util';

import { formatTemplateForUI, isValidTemplate } from '../utils/templateUtils';

export const useTemplateState = () => {
  const { getAccessToken, user } = useWidgetAuth();
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();

  const [templates, setTemplates] = useState<FormattedTemplate[]>([]);

  const features = widgetContext.context?.features;
  const usePlatformTable = isFeatureEnabled(
    features,
    FEATURE_FLAGS.PLATFORM_TEMPLATE_TABLE
  );

  useEffect(() => {
    if (!user?.userId) {
      return;
    }

    const loadTemplates = async () => {
      try {
        const contextTemplates = widgetContext.context?.templates || [];
        const hostTemplates: FormattedTemplate[] = [];

        if (contextTemplates.length > 0) {
          contextTemplates.forEach((template, index) => {
            if (isValidTemplate(template)) {
              hostTemplates.push(
                formatTemplateForUI(
                  template,
                  template.templateId || `host-${index}`,
                  true,
                  template.isLegacy ?? false
                )
              );
            } else {
              logError(
                'useTemplateState',
                `Invalid initialContext template at index ${index}:`,
                template
              );
            }
          });
        }

        const token = await getAccessToken();

        if (!token) {
          setTemplates(hostTemplates);
          return;
        }

        // Select the platform-owned v2 templates table when enabled; otherwise retain the legacy v1 fallback.
        const apiTemplates = usePlatformTable
          ? await documentTemplatesClient.getTemplatesV2({ activeOnly: false })
          : await documentTemplatesClient.getTemplates({ activeOnly: false });

        const fetchedTemplates = apiTemplates.map((t) =>
          // Map API isLegacy from isActive; missing isActive is treated as active.
          formatTemplateForUI(t, t.id, false, t.isActive === false)
        );

        const allTemplates = [...fetchedTemplates, ...hostTemplates];

        setTemplates(allTemplates);
      } catch (err) {
        logError(
          'useTemplateState',
          '❌ Failed to load templates internally:',
          err
        );
        setTemplates([]);
      }
    };

    loadTemplates();
  }, [user?.userId, getAccessToken, usePlatformTable]);

  return {
    templates,
  };
};
